import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, DollarSign, CreditCard, CheckCircle2, Clock, Plus } from "lucide-react";

const formatINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const PAYMENT_METHODS = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque", "Other"];

const InvoiceModal = ({ open, onClose, jobCard }) => {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const [payReference, setPayReference] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [payLoading, setPayLoading] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!jobCard?.id) return;
    try {
      const r = await axios.get(`${API}/invoices/by-jobcard/${jobCard.id}`);
      setExistingInvoice(r.data);
    } catch { setExistingInvoice(null); }
  }, [jobCard]);

  useEffect(() => {
    if (open && jobCard) {
      fetchInvoice();
      setAmount("");
      setShowPaymentForm(false);
      setPayAmount("");
      setPayMethod("Cash");
      setPayReference("");
      setPayNotes("");
    }
  }, [open, jobCard, fetchInvoice]);

  const handleCreate = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/invoices`, { jobcard_id: jobCard.id, amount: parseFloat(amount) });
      toast.success("Invoice created");
      fetchInvoice();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create invoice"); }
    finally { setLoading(false); }
  };

  const handleMarkPaid = async () => {
    if (!existingInvoice) return;
    setLoading(true);
    try {
      const balance = existingInvoice.balance ?? (existingInvoice.amount - (existingInvoice.paid_amount || 0));
      await axios.post(`${API}/invoices/${existingInvoice.id}/payments`, {
        amount: balance, method: "Cash", notes: "Marked as fully paid"
      });
      toast.success("Invoice marked as paid");
      fetchInvoice();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const handleRecordPayment = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) { toast.error("Enter a valid payment amount"); return; }
    setPayLoading(true);
    try {
      await axios.post(`${API}/invoices/${existingInvoice.id}/payments`, {
        amount: parseFloat(payAmount), method: payMethod,
        reference: payReference, notes: payNotes,
      });
      toast.success("Payment recorded");
      setShowPaymentForm(false);
      setPayAmount("");
      setPayReference("");
      setPayNotes("");
      fetchInvoice();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record payment"); }
    finally { setPayLoading(false); }
  };

  if (!jobCard) return null;

  const inv = existingInvoice;
  const paidAmount = inv ? (inv.paid_amount || 0) : 0;
  const totalAmount = inv ? inv.amount : 0;
  const balance = inv ? (inv.balance ?? (totalAmount - paidAmount)) : 0;
  const paidPercent = totalAmount > 0 ? Math.min((paidAmount / totalAmount) * 100, 100) : 0;
  const payments = inv?.payments || [];

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="invoice-modal">
        <DialogHeader>
          <DialogTitle className="text-[16px] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#3B82F6]" />
            {inv ? `Invoice ${inv.invoice_number}` : "Create Invoice"}
          </DialogTitle>
          <DialogDescription>
            Job Card: {jobCard.job_card_number} · {jobCard.customer_name}
          </DialogDescription>
        </DialogHeader>

        {!inv ? (
          /* Create Invoice */
          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-[12px]">Invoice Amount (₹) *</Label>
              <Input data-testid="invoice-amount-input" type="number" value={amount}
                onChange={(e) => setAmount(e.target.value)} className="h-10 mt-1 text-[14px]" placeholder="Enter amount" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onClose(false)} className="text-[12px]">Cancel</Button>
              <Button data-testid="create-invoice-btn" onClick={handleCreate} disabled={loading}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">
                {loading ? "Creating..." : "Create Invoice"}
              </Button>
            </div>
          </div>
        ) : (
          /* Invoice with payment tracking */
          <div className="space-y-4 mt-3">
            {/* Amount summary */}
            <div className="rounded-xl border border-[#F1F5F9] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[11px] text-[#94A3B8] uppercase tracking-wider">Total Amount</p>
                  <p className="text-[22px] font-bold text-[#1E293B]">{formatINR(totalAmount)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-[11px] font-bold ${
                  inv.status === "Paid" ? "bg-[#F0FDF4] text-[#16A34A]" :
                  inv.status === "Partial" ? "bg-[#FEF3C7] text-[#D97706]" :
                  "bg-[#FEF2F2] text-[#DC2626]"}`}>
                  {inv.status}
                </span>
              </div>
              {/* Progress bar */}
              <div className="h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full transition-all bg-[#16A34A]" style={{ width: `${paidPercent}%` }} />
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-[#16A34A] font-semibold">Paid: {formatINR(paidAmount)}</span>
                <span className="text-[#DC2626] font-semibold">Balance: {formatINR(balance)}</span>
              </div>
            </div>

            {/* Payment history */}
            {payments.length > 0 && (
              <div>
                <p className="text-[12px] font-semibold text-[#1E293B] mb-2">Payment History</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {payments.map((p, idx) => (
                    <div key={p.id || idx} className="flex items-center justify-between px-3 py-2 bg-[#F8FAFC] rounded-lg text-[12px]">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-3.5 h-3.5 text-[#64748B]" />
                        <span className="font-medium text-[#1E293B]">{formatINR(p.amount)}</span>
                        <span className="text-[#94A3B8]">via {p.method}</span>
                        {p.reference && <span className="text-[#94A3B8]">· {p.reference}</span>}
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">{new Date(p.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Record payment form */}
            {inv.status !== "Paid" && (
              <>
                {!showPaymentForm ? (
                  <div className="flex gap-2">
                    <Button data-testid="record-payment-btn" onClick={() => setShowPaymentForm(true)}
                      className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5">
                      <Plus className="w-3.5 h-3.5" /> Record Payment
                    </Button>
                    <Button data-testid="mark-paid-btn" onClick={handleMarkPaid} variant="outline"
                      disabled={loading}
                      className="text-[12px] gap-1.5 border-[#D1FAE5] text-[#16A34A] hover:bg-[#F0FDF4]">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {loading ? "..." : "Mark Fully Paid"}
                    </Button>
                  </div>
                ) : (
                  <div className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                    <p className="text-[13px] font-semibold text-[#1E293B]">Record Payment</p>
                    <p className="text-[11px] text-[#94A3B8]">Outstanding balance: {formatINR(balance)}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px]">Amount (₹) *</Label>
                        <Input data-testid="payment-amount" type="number" value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)} className="h-9 mt-1 text-[13px]"
                          placeholder={`Max ${balance}`} max={balance} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Payment Method</Label>
                        <Select value={payMethod} onValueChange={setPayMethod}>
                          <SelectTrigger data-testid="payment-method" className="h-9 mt-1 text-[13px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px]">Reference / Transaction ID</Label>
                      <Input data-testid="payment-reference" value={payReference}
                        onChange={(e) => setPayReference(e.target.value)} className="h-9 mt-1 text-[13px]"
                        placeholder="UPI ref, cheque no., etc." />
                    </div>
                    <div>
                      <Label className="text-[11px]">Notes</Label>
                      <Textarea data-testid="payment-notes" value={payNotes} onChange={(e) => setPayNotes(e.target.value)}
                        className="mt-1 text-[13px]" rows={2} placeholder="Optional" />
                    </div>
                    <div className="flex gap-2 justify-end pt-1">
                      <Button variant="outline" onClick={() => setShowPaymentForm(false)} className="text-[12px]">Cancel</Button>
                      <Button data-testid="submit-payment-btn" onClick={handleRecordPayment} disabled={payLoading}
                        className="bg-[#16A34A] hover:bg-[#15803D] text-white text-[12px]">
                        {payLoading ? "Recording..." : "Record Payment"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {inv.status === "Paid" && (
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F0FDF4] rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-[#16A34A]" />
                <p className="text-[13px] font-semibold text-[#16A34A]">Fully Paid</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceModal;
